/**
 * Unified Real-time Client for the Good Outcomes platform.
 *
 * This client provides a simple, reliable interface for components to subscribe
 * to real-time updates with guaranteed delivery via Redis Streams backend.
 *
 * Features:
 * - Simple subscription API for any entity type
 * - Automatic reconnection with exponential backoff
 * - Message deduplication and ordering
 * - Connection health monitoring
 * - Subscription state management
 * - Type-safe message handling
 */

import { config } from '../config';

export interface RealtimeMessage {
  type: string;
  signal_id?: string;
  entity_type?: string;
  entity_id?: string;
  organization_id?: string;
  data?: any;
  timestamp?: number;
}

export interface SubscriptionHandler {
  (message: RealtimeMessage): void;
}

export interface ConnectionStats {
  isConnected: boolean;
  connectionAttempts: number;
  lastConnected: number | null;
  subscriptionCount: number;
  messagesReceived: number;
  lastMessage: RealtimeMessage | null;
}

export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  FAILED = 'failed'
}

export interface SubscriptionOptions {
  /**
   * Whether to receive historical messages on subscription
   */
  includeHistory?: boolean;

  /**
   * Filter messages by type (e.g., 'signal.progress', 'signal.classified')
   */
  messageTypes?: string[];

  /**
   * Maximum age of messages to process (in milliseconds)
   */
  maxMessageAge?: number;
}

class RealtimeClient {
  private ws: WebSocket | null = null;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private subscriptions = new Map<string, Set<SubscriptionHandler>>();
  private messageHistory = new Map<string, RealtimeMessage[]>();
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingTimer: NodeJS.Timer | null = null;
  private connectionListeners = new Set<(state: ConnectionState) => void>();
  private stats: ConnectionStats;

  // Configuration
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelay = 1000; // 1 second
  private readonly maxReconnectDelay = 30000; // 30 seconds
  private readonly pingInterval = 30000; // 30 seconds
  private readonly messageHistoryLimit = 100;
  private readonly maxMessageAge = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.stats = {
      isConnected: false,
      connectionAttempts: 0,
      lastConnected: null,
      subscriptionCount: 0,
      messagesReceived: 0,
      lastMessage: null
    };
  }

  /**
   * Initialize the real-time client and establish connection
   */
  public async connect(): Promise<void> {
    if (this.connectionState === ConnectionState.CONNECTING ||
        this.connectionState === ConnectionState.CONNECTED) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    this.setConnectionState(ConnectionState.CONNECTING);
    this.stats.connectionAttempts++;

    try {
      // Close existing connection if any
      this.disconnect();

      // Construct WebSocket URL for new reliable endpoint
      // Remove /api from the end if present, since we'll add the full endpoint path
      const baseUrl = config.API_URL.replace(/\/api$/, '');
      const wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      const fullUrl = `${wsUrl}/api/ws/v2/signals?token=${encodeURIComponent(token)}`;

      this.ws = new WebSocket(fullUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);

    } catch (error) {
      console.error('[RealtimeClient] Failed to create WebSocket:', error);
      this.setConnectionState(ConnectionState.FAILED);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the real-time service
   */
  public disconnect(): void {
    this.clearTimers();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;

      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Client disconnect');
      }

      this.ws = null;
    }

    this.setConnectionState(ConnectionState.DISCONNECTED);
  }

  /**
   * Subscribe to real-time updates for a specific entity
   */
  public subscribe(
    entityType: string,
    entityId: string,
    handler: SubscriptionHandler,
    options: SubscriptionOptions = {}
  ): () => void {
    const subscriptionKey = `${entityType}:${entityId}`;

    // Add handler to subscription map
    if (!this.subscriptions.has(subscriptionKey)) {
      this.subscriptions.set(subscriptionKey, new Set());
    }
    this.subscriptions.get(subscriptionKey)!.add(handler);
    this.updateSubscriptionStats();

    // Send subscription message if connected
    if (this.connectionState === ConnectionState.CONNECTED) {
      this.sendSubscriptionMessage(entityType, entityId, 'subscribe');
    }

    console.log(`[RealtimeClient] Subscribed to ${subscriptionKey}`);

    // Return unsubscribe function
    return () => {
      this.unsubscribe(entityType, entityId, handler);
    };
  }

  /**
   * Unsubscribe from real-time updates
   */
  public unsubscribe(entityType: string, entityId: string, handler: SubscriptionHandler): void {
    const subscriptionKey = `${entityType}:${entityId}`;
    const handlers = this.subscriptions.get(subscriptionKey);

    if (handlers) {
      handlers.delete(handler);

      // If no more handlers, remove subscription and notify server
      if (handlers.size === 0) {
        this.subscriptions.delete(subscriptionKey);

        if (this.connectionState === ConnectionState.CONNECTED) {
          this.sendSubscriptionMessage(entityType, entityId, 'unsubscribe');
        }

        console.log(`[RealtimeClient] Unsubscribed from ${subscriptionKey}`);
      }
    }

    this.updateSubscriptionStats();
  }

  /**
   * Subscribe to organization-wide updates
   */
  public subscribeToOrganization(
    organizationId: string,
    handler: SubscriptionHandler,
    options: SubscriptionOptions = {}
  ): () => void {
    return this.subscribe('org', organizationId, handler, options);
  }

  /**
   * Subscribe to specific signal updates
   */
  public subscribeToSignal(
    signalId: string,
    handler: SubscriptionHandler,
    options: SubscriptionOptions = {}
  ): () => void {
    return this.subscribe('signal', signalId, handler, options);
  }

  /**
   * Subscribe to user-specific notifications
   */
  public subscribeToUser(
    userId: string,
    handler: SubscriptionHandler,
    options: SubscriptionOptions = {}
  ): () => void {
    return this.subscribe('user', userId, handler, options);
  }

  /**
   * Get current connection statistics
   */
  public getStats(): ConnectionStats {
    return { ...this.stats };
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Add listener for connection state changes
   */
  public onConnectionStateChange(listener: (state: ConnectionState) => void): () => void {
    this.connectionListeners.add(listener);

    // Immediately notify of current state
    listener(this.connectionState);

    // Return cleanup function
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  /**
   * Send a ping to test connection health
   */
  public ping(): void {
    if (this.connectionState === ConnectionState.CONNECTED && this.ws) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }

  private handleOpen(): void {
    console.log('[RealtimeClient] Connected to real-time service');

    this.setConnectionState(ConnectionState.CONNECTED);
    this.reconnectAttempts = 0;
    this.stats.lastConnected = Date.now();

    // Re-establish all subscriptions
    this.reestablishSubscriptions();

    // Start ping timer
    this.pingTimer = setInterval(() => {
      this.ping();
    }, this.pingInterval) as any;
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: RealtimeMessage = JSON.parse(event.data);

      this.stats.messagesReceived++;
      this.stats.lastMessage = message;

      // Handle connection-level messages
      if (message.type === 'connection') {
        console.log('[RealtimeClient] Connection confirmed:', message);
        return;
      }

      if (message.type === 'pong') {
        return; // Ignore pong responses
      }

      if (message.type === 'subscribed' || message.type === 'unsubscribed') {
        console.log('[RealtimeClient] Subscription acknowledged:', message);
        return;
      }

      // Process data messages
      this.processMessage(message);

    } catch (error) {
      console.error('[RealtimeClient] Failed to parse message:', error);
    }
  }

  private handleError(error: Event): void {
    console.error('[RealtimeClient] WebSocket error:', error);
  }

  private handleClose(event: CloseEvent): void {
    console.log(`[RealtimeClient] Disconnected: ${event.code} ${event.reason}`);

    this.clearTimers();
    this.setConnectionState(ConnectionState.DISCONNECTED);

    // Schedule reconnection if not intentional disconnect
    if (event.code !== 1000) {
      this.scheduleReconnect();
    }
  }

  private processMessage(message: RealtimeMessage): void {
    // Filter old messages
    if (message.timestamp && (Date.now() - message.timestamp) > this.maxMessageAge) {
      return;
    }

    // Store in history for potential replay
    this.addToHistory(message);

    // Route message to appropriate subscribers
    this.routeMessage(message);
  }

  private routeMessage(message: RealtimeMessage): void {
    const subscriptionsToNotify = new Set<string>();

    // Organization-wide messages
    if (message.organization_id) {
      subscriptionsToNotify.add(`org:${message.organization_id}`);
    }

    // Entity-specific messages
    if (message.entity_type && message.entity_id) {
      subscriptionsToNotify.add(`${message.entity_type}:${message.entity_id}`);
    }

    // Signal-specific messages (backward compatibility)
    if (message.signal_id) {
      subscriptionsToNotify.add(`signal:${message.signal_id}`);
    }

    // User-specific messages
    if (message.type === 'user.notification' && message.entity_id) {
      subscriptionsToNotify.add(`user:${message.entity_id}`);
    }

    // FOS sync messages - route to fos:sync subscribers
    if (message.type?.startsWith('fos.')) {
      subscriptionsToNotify.add('fos:sync');
      console.log('[RealtimeClient] Routing FOS message to fos:sync subscribers:', message.type);
    }

    // Notify all relevant subscribers
    subscriptionsToNotify.forEach(subscriptionKey => {
      const handlers = this.subscriptions.get(subscriptionKey);
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(message);
          } catch (error) {
            console.error('[RealtimeClient] Error in message handler:', error);
          }
        });
      }
    });
  }

  private addToHistory(message: RealtimeMessage): void {
    const key = message.entity_type && message.entity_id
      ? `${message.entity_type}:${message.entity_id}`
      : message.signal_id
      ? `signal:${message.signal_id}`
      : 'global';

    if (!this.messageHistory.has(key)) {
      this.messageHistory.set(key, []);
    }

    const history = this.messageHistory.get(key)!;
    history.push(message);

    // Limit history size
    if (history.length > this.messageHistoryLimit) {
      history.shift();
    }
  }

  private sendSubscriptionMessage(entityType: string, entityId: string, action: 'subscribe' | 'unsubscribe'): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: action,
        entity_type: entityType,
        entity_id: entityId
      }));
    }
  }

  private reestablishSubscriptions(): void {
    console.log(`[RealtimeClient] Re-establishing ${this.subscriptions.size} subscriptions`);

    this.subscriptions.forEach((handlers, subscriptionKey) => {
      const [entityType, entityId] = subscriptionKey.split(':');
      this.sendSubscriptionMessage(entityType, entityId, 'subscribe');
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[RealtimeClient] Maximum reconnection attempts reached');
      this.setConnectionState(ConnectionState.FAILED);
      return;
    }

    this.setConnectionState(ConnectionState.RECONNECTING);

    // Exponential backoff with jitter
    const delay = Math.min(
      this.maxReconnectDelay,
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts) + Math.random() * 1000
    );

    console.log(`[RealtimeClient] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect().catch(error => {
        console.error('[RealtimeClient] Reconnection failed:', error);
      });
    }, delay) as any;
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.stats.isConnected = state === ConnectionState.CONNECTED;

      // Notify listeners
      this.connectionListeners.forEach(listener => {
        try {
          listener(state);
        } catch (error) {
          console.error('[RealtimeClient] Error in connection state listener:', error);
        }
      });
    }
  }

  private updateSubscriptionStats(): void {
    this.stats.subscriptionCount = this.subscriptions.size;
  }

  private clearTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

// Global singleton instance
export const realtimeClient = new RealtimeClient();

// Auto-connect when module loads (in browser environment)
if (typeof window !== 'undefined') {
  realtimeClient.connect().catch(error => {
    console.error('[RealtimeClient] Failed to auto-connect:', error);
  });
}